import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/containing-the-agentic-blast-radius';
const BLOG_POST_PUBLISHED_AT = '2026-09-14';
const BLOG_POST_READ_TIME = '6 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
const BLOG_POST_OG_TITLE = 'Containing the agentic blast radius | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  'Bedrock AgentCore gives you microVM isolation and credential custody, but not per-run egress. How we scope an agent’s network and credentials per invocation.';

const NSJAIL_SNIPPET = `# supervisor, per invocation: re-scope this run, then jail the agent
write_policy(tenant)                                   # domains + methods + paths for this run
proxy_bind_credential(tenant)                          # which identity is attached on egress

nsjail -Mo --disable_clone_newns --disable_clone_newpid \\   # user + net ns: only lo, no MMDS
  -u 1000 -g 1000 \\                                         # drop to an unprivileged uid
  --seccomp_policy /run/jail.policy \\                       # block unshare/mount/ptrace/bpf
  --env HTTPS_PROXY=http://127.0.0.1:8888 \\                 # dummy keys + one way out
  --env AWS_ACCESS_KEY_ID=dummy --env AWS_SECRET_ACCESS_KEY=dummy \\
  -- /usr/bin/agent`;

const UNSHARE_SNIPPET = `unshare --user --map-root --net \\        # its own network namespace: no default route, no MMDS
  env -i HTTPS_PROXY=http://127.0.0.1:8888 \\
      AWS_ACCESS_KEY_ID=dummy AWS_SECRET_ACCESS_KEY=dummy \\
    dropto 1000 /usr/bin/agent           # unprivileged uid, dummy keys, one way out`;

const POLICY_SNIPPET = `"endpoint_policy": {
  "default": { "decision": "deny" },
  "allow":  [ { "method": "GET",  "path": "/**" } ],
  "deny":   [ { "method": "GET",  "path": "/repos/your_org/secret/**" },
              { "method": "POST|PUT|PATCH|DELETE", "path": "/**" } ]
}`;

const BlogAgenticBlastRadiusPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T12:00:00.000Z`}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post blog-post--centered">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              Containing the agentic blast radius
            </h1>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Engineering</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>14 Sep 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__content">
            <p>
              Building agents can be a daunting task. You don&apos;t really know how to contain access to the LLM,
              and the blast radius can be larger than humans or apps because of how quickly LLMs can code and make tool
              calls.
            </p>
            <p>
              At Circumvent we use agentic workflows to investigate security findings to help customers triage and
              prioritize their work. This presents us with a unique scenario where our customers trust us with
              their credentials on our agentic workloads. So how do we give an agent enough access to be useful without
              giving it enough access to be dangerous? How do we investigate something for customer A and ensure its
              execution environment is isolated from customer B?
            </p>

            <p>
              For that and more we use Bedrock agentcore. For those of you who haven&apos;t used it, it is
              essentially an isolated microvm designed for agentic use. If you are going deep into AWS and agentcore you
              can use:
            </p>
            <ul>
              <li>Session ids for isolation so each session gets its own microvm</li>
              <li>
                Keep long lived secrets out of the agent and short lived tokens are vended to the agent (
                <a
                  href="https://docs.aws.amazon.com/bedrock-agentcore/latest/devguide/identity.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  agentcore identity
                </a>
                )
              </li>
              <li>
                Per tool auth can be set via policy (
                <a
                  href="https://docs.aws.amazon.com/bedrock-agentcore/latest/devguide/gateway.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  agentcore gateway
                </a>
                )
              </li>
              <li>
                App level egress policies (
                <a
                  href="https://aws.amazon.com/blogs/machine-learning/control-which-domains-your-ai-agents-can-access/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  AWS guide
                </a>
                )
              </li>
            </ul>

            <p>
              But if you&apos;re not looking to opt in fully to agentcore and want to build on lambda microvms, ec2, or
              like us you need per run egress policies. Then the following will show you how to achieve that. This might
              be handy if you want the same runtime to have two different auth boundaries or you want configurable app
              level auth. An example may be if you are stuck with legacy api keys with no scopes (think Atlassian
              API_TOKEN or GitHub personal access token (PAT)) or even if you simply don&apos;t want to opt into the whole
              agentcore ecosystem. In addition to all of that, it is a good way to achieve agentic defense in depth. Even if you do use all the
              native bedrock agentcore features, this will give you an additional layer of security in case anything gets
              through.
            </p>

            <p>
              The simple architecture for this is to separate your control plane (api) from your agent. Think of your
              agent like a lambda function and your control plane being the thing that orchestrates and invokes the
              agent. At Circumvent, we separate them by having a token vending machine that will issue tokens at
              execution time. We do this to limit having long lived tokens as well as scoping them down to only what the
              agent needs. This also allows us to have an audit trail that can give us the tracing needed to show what
              toolcalls and invocations the agent did.
            </p>

            <figure className="blog-diagram">
              <img
                src="/images/blog/arch-token-vending.svg"
                alt="Architecture diagram: user to frontend to API control plane, with a token vending machine minting a scoped token, across a trust boundary into the AgentCore agent sandbox."
                loading="lazy"
                decoding="async"
              />
            </figure>

            <p>
              Once you have that setup you are probably thinking that if we inject the secrets into the agent then what
              is the point since the agent can read them? While it is true that the runtime will have the secret, we can
              limit the actual agent by running a supervisor process in the /invocations endpoint. If you had full
              kernel access you could use something like landlock (which you should if you’re running agents on ec2 etc)
              but for agentcore in that invocations endpoint we can do the following
            </p>

            <pre className="blog-code">
              <code>{NSJAIL_SNIPPET}</code>
            </pre>

            <p>
              <code>nsjail</code> puts the agent in user and network namespaces so it only sees loopback, then drops it
              to an unprivileged user ID (uid). We can also clear its env
              vars so it has no access to creds at all. The unprivileged uid won’t have access to get around it. We check
              to see if agentcore had CAP_NET_ADMIN which would’ve allowed us to program iptables but it didn’t look like
              it was available (correct me if I’m wrong). Nsjail also gives you secure computing (seccomp), which
              basically filters which syscalls a process can make.
            </p>

            <figure className="blog-diagram">
              <img
                src="/images/blog/arch-egress-proxy.svg"
                alt="Diagram of the AgentCore container: a privileged supervisor on port 8080 spawns a jailed agent per invocation, whose only route out is a unix socket to an egress proxy that holds the real credential and appends it on egress to AWS or GitHub."
                loading="lazy"
                decoding="async"
              />
            </figure>

            <p>If you don’t want to use nsjail you can achieve similar with:</p>

            <pre className="blog-code">
              <code>{UNSHARE_SNIPPET}</code>
            </pre>

            <p>This gets you the same separation via namespaces, uid change and clearing out env vars.</p>

            <p>
              Either way the agent then has no actual credentials and everything is attached via the egress policy. This
              is a similar mechanism to bedrock agentcore gateway but we have more flexibility in what can go out by
              defining networking whitelists/blacklists. With this config you essentially run 2 processes inside the
              container: one privileged and the other locked down.
            </p>

            <p>With that config as well as a policy like:</p>

            <pre className="blog-code">
              <code>{POLICY_SNIPPET}</code>
            </pre>

            <p>
              With such a setup you can run, for example, an investigator with a GitHub PAT that denies any
              post/put/patch, then on the next run you can allow it and allow it to open a PR, all without a deploy.
            </p>

            <p>
              You can of course achieve most of this with the managed parts of agentcore gateway. We always recommend
              defense in depth and this allows you some additional peace of mind if you get something else wrong. It also
              allows you to port this over to lambda microvms or any other infrastructure you like. If you made it this
              far, thanks for reading and I hope you learned something about securing agents.
            </p>

            <div className="blog-post__cta">
              <h3>Contain your agents&apos; blast radius</h3>
              <p>Talk to us about running agentic security workflows with per-run isolation.</p>
              <Link to="/#contact" className="btn btn-solid">
                Book a demo
              </Link>
            </div>

            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogAgenticBlastRadiusPage;
